import type { ReactiveController, ReactiveControllerHost } from 'lit';
import type { PopoverListItem } from '../interfaces/popover-list-item.interface.js';

const DEBOUNCE_DELAY = 300;

/**
 * Reactive controller that provides debounced text filtering for PopoverListItems.
 * Uses setTimeout/clearTimeout directly (no RxJS).
 */
export class FilterController implements ReactiveController {
    filterText = '';

    private _host: ReactiveControllerHost;
    private _debounceTimer: ReturnType<typeof setTimeout> | undefined;

    constructor(host: ReactiveControllerHost) {
        this._host = host;
        host.addController(this);
    }

    /**
     * Returns items filtered by label (case-insensitive substring).
     * Hidden items are always excluded. Disabled items remain visible.
     */
    getFilteredItems(items: PopoverListItem[]): PopoverListItem[] {
        const visibleItems = items.filter(item => !item.hidden);

        if (!this.filterText) {
            return visibleItems;
        }

        const needle = this.filterText.toLowerCase();
        return visibleItems.filter(item =>
            item.label.toLowerCase().includes(needle)
        );
    }

    /**
     * Debounced handler for filter input events.
     * Updates filterText and requests host update after 300ms delay.
     */
    handleFilterInput(event: InputEvent): void {
        const value = (event.target as HTMLInputElement).value;

        clearTimeout(this._debounceTimer);
        this._debounceTimer = setTimeout(() => {
            this.filterText = value;
            this._host.requestUpdate();
        }, DEBOUNCE_DELAY);
    }

    /**
     * Drops the filter at once: empties `filterText` and cancels a pending debounce, so
     * text typed just before cannot come back 300 ms later. Does not request an update —
     * the caller does, together with clearing the visible input.
     */
    reset(): void {
        clearTimeout(this._debounceTimer);
        this._debounceTimer = undefined;
        this.filterText = '';
    }

    /**
     * Clears pending debounce timer on host disconnect.
     */
    hostDisconnected(): void {
        clearTimeout(this._debounceTimer);
        this._debounceTimer = undefined;
    }
}
